import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Smartphone, Loader2, CheckCircle2, XCircle, Search, FileSignature, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { TargetAcksLogDialog } from "./TargetAcksLogDialog";
import { ConsentsLogDialog } from "./ConsentsLogDialog";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { personMatch } from "../lib/searchText";

const KPI = ({ label, value, tone }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`font-heading text-2xl font-bold tabular-nums ${tone || "text-slate-800"}`}>{value}</p>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar-SA", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16).replace("T", " "); }
};

export const PortalUsagePanel = () => {
  const { dir } = useLang();
  const [data, setData] = useState({ summary: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [consentsOpen, setConsentsOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all | activated | not

  useEffect(() => {
    (async () => {
      try { setData(await api.portalUsage()); }
      catch { toast.error("تعذّر تحميل بيانات الاستخدام"); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = useMemo(() => {
    let r = data.items || [];
    if (filter === "activated") r = r.filter((x) => x.activated);
    if (filter === "not") r = r.filter((x) => !x.activated);
    const s = q.trim();
    if (s) r = r.filter((x) => personMatch(x, s));
    return r;
  }, [data.items, q, filter]);

  const s = data.summary || {};
  const pct = s.total ? Math.round(((s.activated || 0) / s.total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="portal-usage-panel" dir={dir}>
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Smartphone className="h-5 w-5" /></div>
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-900">استخدام تطبيق المندوب</h2>
          <p className="text-xs text-slate-500">من فعّل حسابه ودخل التطبيق فعليًا</p>
        </div>
        <div className="ms-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setLogOpen(true)} data-testid="target-acks-log-button" className="gap-1.5"><FileSignature className="h-4 w-4" /> سجل الإقرارات</Button>
          <Button variant="outline" size="sm" onClick={() => setConsentsOpen(true)} data-testid="consents-log-button" className="gap-1.5"><ShieldCheck className="h-4 w-4" /> سجل التعهّدات</Button>
        </div>
      </div>
      <TargetAcksLogDialog open={logOpen} onOpenChange={setLogOpen} />
      <ConsentsLogDialog open={consentsOpen} onOpenChange={setConsentsOpen} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPI label="إجمالي المناديب" value={s.total || 0} />
        <KPI label="دخلوا التطبيق" value={s.activated || 0} tone="text-emerald-600" />
        <KPI label="لم يدخلوا بعد" value={s.not_activated || 0} tone="text-rose-600" />
        <KPI label="نسبة التفعيل" value={`${pct}%`} tone="text-blue-600" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="portal-usage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم الإقامة أو الجوال" className="pr-9" />
        </div>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-semibold">
          {[["all", "الكل"], ["activated", "دخلوا"], ["not", "لم يدخلوا"]].map(([k, l]) => (
            <button key={k} data-testid={`portal-usage-filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-md px-3 py-1.5 transition-colors ${filter === k ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">لا توجد نتائج</div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="px-3 py-2.5 text-right">المندوب</th>
              <th className="px-3 py-2.5">رقم الإقامة</th>
              <th className="px-3 py-2.5">الحالة</th>
              <th className="px-3 py-2.5">أول دخول</th>
              <th className="px-3 py-2.5">آخر دخول</th>
              <th className="px-3 py-2.5">مرات الدخول</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`portal-usage-row-${r.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2.5 text-right"><div className="font-semibold text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.branch || "—"}</div></td>
                  <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600" dir="ltr">{r.iqama_number || "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {r.activated ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> دخل التطبيق</span>
                        {!r.login_count && !r.last_login_at && <span className="text-[10px] text-slate-400">فعّل حسابه سابقًا</span>}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"><XCircle className="h-3.5 w-3.5" /> لم يدخل</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs text-slate-500" dir="ltr">{fmtDate(r.first_login_at)}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-slate-500" dir="ltr">{fmtDate(r.last_login_at)}</td>
                  <td className="px-3 py-2.5 text-center font-mono-nums font-semibold text-slate-700">{r.login_count || (r.activated ? "—" : 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
